import React, { useState } from "react";
import Layout from "../components/Layout";
import DropzoneUploader from "../components/DropzoneUploader";
import ToothChart from "../components/ToothChart";
import {
  Upload,
  FileText,
  Mail,
  User,
  MessageSquare,
  Truck,
  CheckCircle,
} from "lucide-react";

const Dropbox = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    deliveryMethod: "",
    files: [] as File[],
  });

  const [selectedTeethMap, setSelectedTeethMap] = useState<
    Record<number, string>
  >({});
  const [xmlFileName, setXmlFileName] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const colors = ["#22c55e", "#3b82f6", "#facc15", "#f472b6", "#8b5cf6"];

  const [showSuccess, setShowSuccess] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileDrop = async (droppedFiles: File[]) => {
    setFormData((prev) => ({
      ...prev,
      files: [...prev.files, ...droppedFiles],
    }));

    let updatedMap: Record<number, string> = { ...selectedTeethMap };

    let colorIndex =
      Object.keys(selectedTeethMap).length > 0
        ? Object.values(selectedTeethMap)
            .map((c) => colors.indexOf(c))
            .filter((i) => i !== -1)
            .reduce((max, cur) => Math.max(max, cur), 0) + 1
        : 0;

    for (const file of droppedFiles) {
      if (
        file.name.endsWith(".xml") ||
        file.name.endsWith(".constructionInfo")
      ) {
        const text = await file.text();
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(text, "text/xml");

        const toothNodes = Array.from(xmlDoc.getElementsByTagName("Tooth"));
        const numbers = toothNodes
          .map((node) => {
            const num = node.getElementsByTagName("Number")[0]?.textContent;
            return num ? parseInt(num, 10) : null;
          })
          .filter((n) => n !== null) as number[];

        const color = colors[colorIndex % colors.length];
        colorIndex++;

        numbers.forEach((num) => {
          updatedMap[num] = color;
        });

        setXmlFileName(file.name);
      }
    }

    setSelectedTeethMap(updatedMap);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const form = new FormData();
    form.append("name", formData.name);
    form.append("email", formData.email);
    form.append("subject", formData.subject);
    form.append("message", formData.message);
    form.append("deliveryMethod", formData.deliveryMethod);

 // 🟢 ADĂUGĂ ACEASTĂ LINIE: Trimite lista de dinți selectați convertită în text
    form.append("toothMapping", JSON.stringify(Object.keys(selectedTeethMap)));


    // Adaugă fișierele
    formData.files.forEach((file) => {
      form.append("files", file);
    });

    try {
      const response = await fetch(
        "https://felixdent-backend.onrender.com/api/dropbox-upload",
        {
          method: "POST",
          body: form,
        },
      );

      const result = await response.json();

      if (result.success) {
        // Arată mesajul de success
        setShowSuccess(true);

        // Resetează formularul
        setFormData({
          name: "",
          email: "",
          subject: "",
          message: "",
          deliveryMethod: "",
          files: [],
        });
        setSelectedTeethMap({});
        setXmlFileName(null);

        // Ascunde mesajul după 3 secunde
        setTimeout(() => {
          setShowSuccess(false);
        }, 3000);
      } else {
        alert("Eroare: " + result.error);
      }
    } catch (error) {
      alert("Eroare de server. Încearcă din nou.");
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen text-gray-900 dark:text-white transition-all duration-300">
        <main className="flex-grow flex flex-col items-center px-4 py-16">
          
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-[#025951] dark:bg-gradient-to-r dark:from-emerald-400 dark:to-emerald-300 dark:bg-clip-text dark:text-transparent uppercase tracking-wide">
              Secure File Upload
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Upload your dental files securely and track your orders with our
              advanced tooth mapping system.
            </p>

            <div className="w-24 h-1 bg-green dark:bg-gradient-to-r from-[#025951] to-emerald-400 mx-auto rounded-full mt-6"></div>

            {/* ADAUGĂ ACEASTĂ LINIE VERDE */}
            <div className="mt-4 mb-3 p-3 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-600 rounded-lg max-w-4xl mx-auto">
              <p className="text-green dark:text-green-200 text-sm font-medium text-center">
                This form is only for approved clients. Do not use this form if
                you are a first-time client, please contact us for details.
              </p>
            </div>

            {/* Upload Form */}
            <div className="bg-white text-black dark:text-white dark:bg-neutral-600 p-8 rounded-2xl shadow-lg border border-gray-200 dark:border-neutral-500">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-green dark:bg-gradient-to-br from-[#025951] to-emerald-400 rounded-lg flex items-center justify-center mr-4">
                  <Upload className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-black dark:text-white">
                  Upload Files
                </h2>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="flex items-center text-sm font-semibold text-black dark:text-gray-300 mb-2">
                      <User className="w-4 h-4 mr-2" />
                      Your Name *
                    </label>
                    <input
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      className="w-full px-4 py-3 rounded-lg bg-neutral-50 dark:bg-neutral-500 border border-gray-200 dark:border-neutral-500 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-[#025951] dark:focus:ring-emerald-400 focus:border-transparent transition-all duration-200"
                    />
                  </div>

                  <div>
                    <label className="flex items-center text-sm font-semibold text-black dark:text-gray-300 mb-2">
                      <Mail className="w-4 h-4 mr-2" />
                      Email Address *
                    </label>
                    <input
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your.email@example.com"
                      className="w-full px-4 py-3 rounded-lg bg-neutral-50 dark:bg-neutral-500 border border-gray-200 dark:border-neutral-500 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-[#025951] dark:focus:ring-emerald-400 focus:border-transparent transition-all duration-200"
                    />
                  </div>
                </div>

                <div>
                  <label className="flex items-center text-sm font-semibold text-black dark:text-gray-300 mb-2">
                    <FileText className="w-4 h-4 mr-2" />
                    Subject *
                  </label>
                  <input
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="Brief description of your project"
                    className="w-full px-4 py-3 rounded-lg bg-neutral-50 dark:bg-neutral-500 border border-gray-200 dark:border-neutral-500 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-[#025951] dark:focus:ring-emerald-400 focus:border-transparent transition-all duration-200"
                  />
                </div>

                <div>
                  <label className="flex items-center text-sm font-semibold text-black dark:text-gray-300 mb-2">
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Message (Optional)
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Additional details about your project..."
                    className="w-full px-4 py-3 rounded-lg bg-neutral-50 dark:bg-neutral-500 border border-gray-200 dark:border-neutral-500 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-[#025951] dark:focus:ring-emerald-400 focus:border-transparent transition-all duration-200 resize-none"
                  />
                </div>

                <div>
                  {/* ADAUGĂ ACESTE LINII DE INFORMAȚII */}
                  <div className="mb-3 p-3 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-600 rounded-lg">
                    <h4 className="font-semibold text-green dark:text-green-200 mb-2">
                      Upload Guidelines:
                    </h4>
                    <ul className="text-sm text-green dark:text-green-200 space-y-1">
                      <li>• The maximum size per file is up to 100MB</li>
                      <li>• You can upload multiple files at a time</li>
                      <li>• Accepted formats: .zip, .rar, .7z, .stl, .dcm</li>
                    </ul>
                  </div>

                  <DropzoneUploader onFilesSelected={handleFileDrop} />
                  {formData.files.length > 0 && (
                    <div className="mt-4 p-4 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg border border-emerald-200 dark:border-emerald-600">
                      <h4 className="font-semibold text-emerald-800 dark:text-emerald-300 mb-2">
                        Uploaded Files:
                      </h4>
                      <ul className="space-y-1">
                        {formData.files.map((file, index) => (
                          <li
                            key={index}
                            className="flex items-center text-sm text-emerald-700 dark:text-emerald-400"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            {file.name}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {xmlFileName && (
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-600">
                    <p className="text-blue-800 dark:text-blue-300 text-center">
                      🦷 <strong>Teeth mapping loaded from:</strong>{" "}
                      {xmlFileName}
                    </p>
                  </div>
                )}

                <div>
                  <label className="flex items-center text-sm font-semibold text-black dark:text-gray-300 mb-2">
                    <Truck className="w-4 h-4 mr-2" />
                    Delivery Method *
                  </label>
                  <select
                    name="deliveryMethod"
                    required
                    value={formData.deliveryMethod}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg bg-neutral-50 dark:bg-neutral-500 border border-gray-200 dark:border-neutral-500 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-[#025951] dark:focus:ring-emerald-400 focus:border-transparent transition-all duration-200"
                  >
                    <option value="">Select Delivery Method</option>
                    <option value="Royal Mail Tracked 24h - £5">
                      Royal Mail Tracked 24h - £5
                    </option>
                    <option value="Royal Mail Special Delivery Guaranteed by 1pm - £9">
                      Royal Mail Special Delivery Guaranteed by 1pm - £9
                    </option>
                    <option value="Ship With Another Order">
                      Ship With Another Order
                    </option>
                  </select>
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-green dark:bg-gradient-to-br from-[#025951] to-emerald-400 text-white font-semibold py-4 px-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Uploading...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center">
                        <Upload className="w-5 h-5 mr-2" />
                        Submit Files
                      </span>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Tooth Chart */}
            <div className="mt-8 bg-green-300 dark:bg-neutral-600 p-8 rounded-2xl shadow-lg border border-gray-200 dark:border-neutral-500">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-white mb-2">
                  Tooth Mapping
                </h2>
                <p className="text-gray-100 dark:text-gray-100">
                  Visual representation of selected teeth from uploaded files
                </p>
              </div>

              <div className="flex justify-center">
                <ToothChart selectedTeeth={selectedTeethMap} />
              </div>

              {Object.keys(selectedTeethMap).length > 0 && (
                <div className="mt-6 p-4 bg-neutral-50 dark:bg-neutral-600 rounded-lg">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                    Selected Teeth:
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {Object.keys(selectedTeethMap).join(", ")}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Info Section */}
          <div className="max-w-4xl w-full mt-16">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-6">
                <div className="w-16 h-16 bg-green dark:bg-gradient-to-br from-[#025951] to-emerald-400 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Upload className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                  Secure Upload
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Your files are encrypted and securely transmitted
                </p>
              </div>

              <div className="text-center p-6">
                <div className="w-16 h-16 bg-green dark:bg-gradient-to-br from-[#025951] to-emerald-400 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                  File Processing
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Automatic detection and processing of dental files
                </p>
              </div>

              <div className="text-center p-6">
                <div className="w-16 h-16 bg-green dark:bg-gradient-to-br from-[#025951] to-emerald-400 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Truck className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                  Fast Delivery
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Multiple delivery options to suit your needs
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Success Overlay */}
      {showSuccess && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-neutral-800 p-8 rounded-2xl shadow-2xl text-center max-w-md mx-4">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Upload Successful!
            </h3>
            <p className="text-gray-600 dark:text-gray-300">
              We will respond within 24 hours.
            </p>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Dropbox;
